import { calculateMortgagePayment } from '@/components/calculator/supreme-intelligence/utils/mortgageUtils';

export interface AmortizationData {
  year: string;
  principal: number;
  interest: number;
}

export interface CostScenarioData {
  name: string;
  monthlyPayment: number;
  upfrontCost: number;
  totalCost: number;
}

export interface EquityGrowthData {
  year: string;
  homeValue: number;
  loanBalance: number;
  equity: number;
}

export interface MortgageInputs {
  homePrice: number;
  downPayment: number;
  interestRate: number;
  homeInsurance: number;
  propertyTax: number;
  closingCosts: { total: number };
}

export const generateAmortizationChartData = (
  mortgageData: MortgageInputs, 
  termYears: number = 30
): AmortizationData[] => {
  const data: AmortizationData[] = [];
  const { homePrice, downPayment, interestRate } = mortgageData;
  const loanAmount = homePrice - downPayment;
  const monthlyRate = interestRate / 100 / 12;
  const monthlyPayment = calculateMortgagePayment(loanAmount, interestRate, termYears);
  
  let remainingBalance = loanAmount;
  
  for (let year = 1; year <= termYears; year++) {
    let yearlyPrincipal = 0;
    let yearlyInterest = 0;
    
    for (let month = 1; month <= 12; month++) {
      if (remainingBalance <= 0) break;
      
      const interestPayment = remainingBalance * monthlyRate;
      const principalPayment = Math.min(monthlyPayment - interestPayment, remainingBalance);
      
      yearlyPrincipal += principalPayment;
      yearlyInterest += interestPayment;
      remainingBalance -= principalPayment;
    }
    
    data.push({
      year: `Year ${year}`,
      principal: Math.round(yearlyPrincipal),
      interest: Math.round(yearlyInterest)
    });
  }
  
  return data;
};

export const generateCostScenariosChartData = (
  mortgageData: MortgageInputs, 
  termYears: number = 30
): CostScenarioData[] => {
  const { homePrice, downPayment, interestRate, homeInsurance, propertyTax, closingCosts } = mortgageData;
  const loanAmount = homePrice - downPayment;
  
  // Calculate monthly taxes and insurance
  const monthlyPropertyTax = propertyTax / 12;
  const monthlyInsurance = homeInsurance / 12;
  const monthlyTaxesAndInsurance = monthlyPropertyTax + monthlyInsurance;
  
  // Current plan
  const currentMonthlyPI = calculateMortgagePayment(loanAmount, interestRate, termYears);
  const currentMonthlyTotal = currentMonthlyPI + monthlyTaxesAndInsurance;
  
  // 20% down scenario
  const twentyDownAmount = homePrice * 0.2;
  const twentyDownLoan = homePrice - twentyDownAmount;
  const twentyDownMonthlyPI = calculateMortgagePayment(twentyDownLoan, interestRate, termYears);
  const twentyDownMonthlyTotal = twentyDownMonthlyPI + monthlyTaxesAndInsurance;
  
  // Different term scenario
  const altTermYears = termYears === 15 ? 30 : 15;
  const altTermMonthlyPI = calculateMortgagePayment(loanAmount, interestRate, altTermYears);
  const altTermMonthlyTotal = altTermMonthlyPI + monthlyTaxesAndInsurance;
  
  return [
    {
      name: 'Current Plan',
      monthlyPayment: Math.round(currentMonthlyTotal),
      upfrontCost: Math.round(downPayment + closingCosts.total),
      totalCost: Math.round(currentMonthlyTotal * termYears * 12 + downPayment + closingCosts.total)
    },
    {
      name: '20% Down',
      monthlyPayment: Math.round(twentyDownMonthlyTotal),
      upfrontCost: Math.round(twentyDownAmount + closingCosts.total),
      totalCost: Math.round(twentyDownMonthlyTotal * termYears * 12 + twentyDownAmount + closingCosts.total)
    },
    {
      name: `${altTermYears}-Year Loan`,
      monthlyPayment: Math.round(altTermMonthlyTotal),
      upfrontCost: Math.round(downPayment + closingCosts.total),
      totalCost: Math.round(altTermMonthlyTotal * altTermYears * 12 + downPayment + closingCosts.total)
    }
  ];
};

export const generateEquityGrowthChartData = (
  mortgageData: MortgageInputs, 
  termYears: number = 30
): EquityGrowthData[] => {
  const data: EquityGrowthData[] = [];
  const { homePrice, downPayment, interestRate } = mortgageData;
  const loanAmount = homePrice - downPayment;
  const appreciationRate = 0.03; // 3% annual appreciation
  const monthlyPayment = calculateMortgagePayment(loanAmount, interestRate, termYears);
  const monthlyRate = interestRate / 100 / 12;
  
  let currentValue = homePrice;
  let remainingLoan = loanAmount;
  
  const stepSize = Math.max(2, Math.floor(termYears / 15)); // Adjust step size based on term
  
  for (let year = stepSize; year <= termYears; year += stepSize) {
    currentValue *= Math.pow(1 + appreciationRate, stepSize);
    
    // Calculate remaining loan balance after payments
    for (let months = 0; months < stepSize * 12; months++) {
      if (remainingLoan <= 0) break;
      const interestPayment = remainingLoan * monthlyRate;
      const principalPayment = Math.min(monthlyPayment - interestPayment, remainingLoan);
      remainingLoan -= principalPayment;
    }
    
    data.push({
      year: `Year ${year}`,
      homeValue: Math.round(currentValue),
      loanBalance: Math.max(0, Math.round(remainingLoan)),
      equity: Math.round(currentValue - Math.max(0, remainingLoan))
    });
  }
  
  return data;
};